/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { SpinnerSpec, TableInfoSpec } from "../../../shared/contracts";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

export class SpinnerData extends ItemData {
  public center: Vertex2D;
  /** Degrees. */
  public rotation: number = 0;
  public szMaterial?: string;
  public showBracket: boolean = true;
  public height: number = 60;
  public length: number = 80;
  public damping: number = 0.9879;
  /** Degrees. 0 and 0: free spin. */
  public angleMax: number = 0;
  /** Degrees. */
  public angleMin: number = 0;
  public elasticity: number = 0.3;
  public isVisible: boolean = true;
  public szSurface?: string;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: SpinnerSpec, _info?: TableInfoSpec): SpinnerData {
    const data = new SpinnerData(spec.name, toVertex2D(spec.center));
    data.length = spec.length;
    data.height = spec.height;
    data.rotation = spec.rotation;
    data.damping = spec.damping ?? data.damping;
    data.elasticity = spec.elasticity ?? data.elasticity;
    // upstream: correct angle inversions
    const a = spec.angleMin ?? 0;
    const b = spec.angleMax ?? 0;
    data.angleMin = Math.min(a, b);
    data.angleMax = Math.max(a, b);
    data.showBracket = spec.showBracket ?? data.showBracket;
    data.isVisible = spec.isVisible ?? true;
    data.szMaterial = spec.szMaterial;
    data.szSurface = spec.szSurface;
    return data;
  }
}
